/**
 * Admin "Interested Deals" section — surfaces deals that have one or more
 * investors expressing interest. Click a row to see the list of interested
 * investors and assign the deal directly from there.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Heart,
  MapPin,
  Users,
  UserPlus,
  Mail,
  Phone,
  ChevronRight,
  Loader2,
  RefreshCw,
} from "lucide-react";

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatCurrency = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US")}`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const InterestedDealsSection = ({ onDealAssigned }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(null); // {deal_id, ...}
  const [investors, setInvestors] = useState([]);
  const [loadingInvestors, setLoadingInvestors] = useState(false);
  const [assigningInvestorId, setAssigningInvestorId] = useState(null);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/deals/interested`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setDeals(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load interested deals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return deals;
    const s = search.toLowerCase();
    return deals.filter((d) =>
      [d.property_address, d.city, d.state, d.zip_code]
        .filter(Boolean)
        .some((x) => x.toLowerCase().includes(s))
    );
  }, [deals, search]);

  const openInvestorsForDeal = async (deal) => {
    setSelectedDeal(deal);
    setLoadingInvestors(true);
    setInvestors([]);
    try {
      const { data } = await axios.get(
        `${API}/admin/deals/${deal.deal_id}/interested-investors`,
        { withCredentials: true, headers: getAuthHeaders() }
      );
      setInvestors(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load investors");
    } finally {
      setLoadingInvestors(false);
    }
  };

  const assignToInvestor = async (investorId) => {
    if (!selectedDeal) return;
    setAssigningInvestorId(investorId);
    try {
      await axios.post(
        `${API}/admin/deals/${selectedDeal.deal_id}/assign`,
        { investor_id: investorId, notes: "Assigned from interested list" },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success("Deal assigned to investor");
      setSelectedDeal(null);
      setInvestors([]);
      await fetchDeals();
      onDealAssigned?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign deal");
    } finally {
      setAssigningInvestorId(null);
    }
  };

  return (
    <Card data-testid="interested-deals-section">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Heart className="w-5 h-5 text-rose-500" />
            Interested Deals
            {deals.length > 0 && (
              <Badge variant="secondary" className="ml-2">{deals.length}</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search address, city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-56"
              data-testid="interested-search"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDeals}
              disabled={loading}
              data-testid="interested-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Loading interested deals…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Heart className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {search ? "No deals match your search." : "No investor interest yet — once a deal goes live and an investor expresses interest, it will appear here."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Property</th>
                  <th className="px-3 py-2 hidden md:table-cell">Asking</th>
                  <th className="px-3 py-2 hidden md:table-cell">ARV</th>
                  <th className="px-3 py-2 text-center">Interested</th>
                  <th className="px-3 py-2 hidden lg:table-cell">Latest</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr
                    key={d.deal_id}
                    className="border-t border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => openInvestorsForDeal(d)}
                    data-testid={`interested-row-${d.deal_id}`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{d.property_address}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {d.city}, {d.state} {d.zip_code}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell whitespace-nowrap">{formatCurrency(d.asking_price)}</td>
                    <td className="px-3 py-2.5 hidden md:table-cell whitespace-nowrap">{formatCurrency(d.estimated_arv)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Badge variant="outline" className="gap-1">
                        <Users className="w-3 h-3" />
                        {d.interest_count}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(d.latest_interest_at)}
                    </td>
                    <td className="px-3 py-2.5 text-right">

                     

                      <Button variant="ghost" size="sm" className="h-7 px-2">
                        View
                        <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </Button>


                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Interested investors — full table dialog */}
      <Dialog open={!!selectedDeal} onOpenChange={(o) => !o && setSelectedDeal(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Interested Investors
              {investors.length > 0 && (
                <Badge variant="secondary" className="ml-1">{investors.length}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedDeal && (
                <>
                  {selectedDeal.property_address} — {selectedDeal.city}, {selectedDeal.state}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingInvestors ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Loading investors…
            </div>
          ) : investors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No interested investors yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border max-h-[65vh] overflow-y-auto" data-testid="interested-investors-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground sticky top-0">
                  <tr>
                    <th className="px-3 py-2">Investor</th>
                    <th className="px-3 py-2 hidden md:table-cell">Email</th>
                    <th className="px-3 py-2 hidden lg:table-cell">Phone</th>
                    <th className="px-3 py-2 hidden lg:table-cell">Liquid Capital</th>
                    <th className="px-3 py-2 hidden md:table-cell">Note</th>
                    <th className="px-3 py-2 whitespace-nowrap">Expressed</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {investors.map((row) => (
                    <tr
                      key={row.user_id}
                      className="border-t border-border hover:bg-muted/30 align-top"
                      data-testid={`investor-row-${row.user_id}`}
                    >
                      <td className="px-3 py-2.5">
                        <p className="font-medium truncate max-w-[180px]">
                          {row.investor?.name || row.user_id}
                        </p>
                        {row.investor?.email && (
                          <p className="text-xs text-muted-foreground md:hidden flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[160px]">{row.investor.email}</span>
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        {row.investor?.email ? (
                          <span className="flex items-center gap-1 text-xs">
                            <Mail className="w-3 h-3 text-muted-foreground" />
                            <span className="truncate max-w-[180px]">{row.investor.email}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 hidden lg:table-cell">
                        {row.investor?.phone ? (
                          <span className="flex items-center gap-1 text-xs">
                            <Phone className="w-3 h-3 text-muted-foreground" />
                            {row.investor.phone}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 hidden lg:table-cell text-xs">
                        {row.investor?.available_capital || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        {row.note ? (
                          <p className="text-xs italic text-foreground whitespace-pre-wrap max-w-[220px] line-clamp-3" title={row.note}>
                            "{row.note}"
                          </p>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(row.expressed_at)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button
                          size="sm"
                          onClick={() => assignToInvestor(row.user_id)}
                          disabled={assigningInvestorId !== null}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`assign-btn-${row.user_id}`}
                        >
                          {assigningInvestorId === row.user_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <UserPlus className="w-3.5 h-3.5 mr-1" />
                              Assign
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InterestedDealsSection;
